import { useTranslation } from 'next-i18next';

import { forwardRef, useEffect, useMemo, useState } from 'react';

import { Box, Button, Flex, HStack, Text, VStack } from '@chakra-ui/react';

import { formatHelpers, RegexConst } from '@nocowanie/core';
import { Avatar, useLineCount, usePluralisationTranslation } from '@nocowanie/common-ui';
import { IconsLinear } from '@nocowanie/icons';

import { RatingRate } from '@app/components/molecules/rating-rate';
import { REVIEW_ITEM_DEFAULT_LINE_HEIGHT } from '@app/consts';
import { formatReviewDate, getGuestCategoryDisplayInfo, getGuestCategoryIcon } from '@app/helpers';
import { useDrawerState } from '@app/hooks';
import { accommodationStaticSlice, useAppSelector } from '@app/state';

import { ReviewItemProps } from './review-item.props';

export const ReviewItem = forwardRef<HTMLDivElement, any>(
    (
        {
            review,
            isExpanded = false,
            onExpandClick,
            defaultLineClamp = REVIEW_ITEM_DEFAULT_LINE_HEIGHT,
            isPreview = false,
            highlightedQuery,
            ...props
        }: ReviewItemProps,
        ref,
    ) => {
        const { t } = useTranslation('accommodation', {
            keyPrefix: 'reviews',
        });
        const pluralizeTranslation = usePluralisationTranslation(
            'accommodation',
            'guestsNightsLabel',
        );
        const nightsCount = review.stayNights
            ? pluralizeTranslation(review.stayNights, 'night')
            : null;

        const selectApartmentById = useAppSelector(
            accommodationStaticSlice.selectors.selectApartmentById,
        );

        const apartmentInfo = useMemo(() => {
            if (isPreview) return;
            // roomId will be added to the review object in the future
            return selectApartmentById(review?.roomId ?? null);
        }, [review.roomId, selectApartmentById, isPreview]);

        const guestCategoryInfo = useMemo(
            () => getGuestCategoryDisplayInfo(review.guest?.category),
            [review.guest?.category],
        );

        const { average, guest } = review;
        const [expand, setExpand] = useState(isExpanded);
        const [showTranslated, setShowTranslated] = useState(!!review.translatedContent);
        const username = guest?.name
            ? formatHelpers.formatUsername(guest.name)
            : t('usernamePlaceholder');
        const lineHook = useLineCount<HTMLDivElement>();
        const [showExpandButton, setShowExpandButton] = useState<boolean>(isPreview);

        const { isDrawerPending, openDrawer } = useDrawerState(() => onExpandClick?.());

        const handleSingleReview = onExpandClick ? () => openDrawer() : () => setExpand(!expand);

        useEffect(() => {
            if (!isPreview) {
                return;
            }

            if (lineHook.lineCount > defaultLineClamp) {
                setShowExpandButton(true);
            } else {
                setShowExpandButton(false);
            }
        }, [defaultLineClamp, isPreview, lineHook]);

        const { sanitizedContent, sanitizedTranslatedContent } = useMemo(() => {
            const htmlRegex = RegexConst.HTML_TAGS_WITHOUT_EM;
            const hasHighlightQuery =
                typeof highlightedQuery === 'string' && highlightedQuery.length > 0;

            const content = hasHighlightQuery
                ? review.highlight?.['content.stemfold']?.[0] ||
                  review.highlight?.['content.autocomplete']?.[0] ||
                  review.highlight?.content?.[0]
                : review.content;

            const translatedContent = hasHighlightQuery
                ? review.highlight?.['translatedContent.stemfold']?.[0] ||
                  review.highlight?.['translatedContent.autocomplete']?.[0] ||
                  review.highlight?.translatedContent?.[0]
                : review.translatedContent;

            return {
                sanitizedContent: content?.replace(htmlRegex, '').replace('\n', '<br />'),
                sanitizedTranslatedContent: translatedContent
                    ?.replace(htmlRegex, '')
                    .replace('\n', '<br />'),
            };
        }, [highlightedQuery, review.highlight, review.content, review.translatedContent]);

        return (
            <Box
                borderInline={isPreview ? '1px solid' : 'none'}
                borderTop={isPreview ? '1px solid' : 'none'}
                borderBottom={'1px solid'}
                borderColor={'{colors.border}'}
                borderRadius={isPreview ? 'md' : 'none'}
                py={4}
                px={isPreview ? 4 : 0}
                ref={ref}
                data-testid={'review-item'}
                {...props}
            >
                <Flex gap={'gutterWidth'} alignItems={'center'}>
                    <Avatar initial={guest?.name?.['0']} data-testid={'review-item-avatar'} />
                    <VStack align={'start'} gap={0}>
                        <Box
                            fontSize={'md'}
                            fontWeight={'bold'}
                            lineClamp={1}
                            title={username}
                            data-testid={'review-item-guest-name'}
                        >
                            {username}
                        </Box>
                        <Text as={'span'} fontSize={'xs'}>
                            <Text as={'span'} data-testid={'review-item-date'}>
                                {formatReviewDate(review.date, t)}
                            </Text>
                            {!isPreview && review.stayNights ? (
                                <>
                                    <Text
                                        as={'span'}
                                        mx={2}
                                        aria-hidden={true}
                                        display={'inline-block'}
                                        transform={'scale(0.75)'}
                                    >
                                        &bull;
                                    </Text>
                                    <Text
                                        as={'span'}
                                        color={'gray.500'}
                                        data-testid={'review-item-nights'}
                                    >
                                        {nightsCount}
                                    </Text>
                                </>
                            ) : null}
                        </Text>
                    </VStack>

                    <RatingRate ratingAverage={average} size={'sm'} ml={'auto'} />
                </Flex>
                <Box
                    fontSize={{
                        base: 'sm',
                        md: 'md',
                    }}
                    mt={3}
                    lineClamp={
                        expand || !sanitizedContent.length || !showExpandButton
                            ? undefined
                            : defaultLineClamp
                    }
                    wordBreak={'break-word'}
                    color={sanitizedContent.length ? undefined : 'gray.500'}
                    data-testid={'review-item-content'}
                    ref={lineHook.ref}
                >
                    {sanitizedContent.length ? (
                        <Box
                            display={
                                'inline'
                            } /* iOS safari needs display inline so that line clamp would work properly */
                            dangerouslySetInnerHTML={{
                                __html:
                                    showTranslated && sanitizedTranslatedContent
                                        ? sanitizedTranslatedContent
                                        : sanitizedContent,
                            }}
                            css={{
                                '& > em': {
                                    borderRadius: 'md',
                                    bgColor: 'yellow.400',
                                },
                            }}
                        />
                    ) : (
                        t('emptyText')
                    )}
                </Box>
                {sanitizedContent.length && showExpandButton ? (
                    <Button
                        size={'sm'}
                        mt={1}
                        onClick={handleSingleReview}
                        variant={'link'}
                        loading={isDrawerPending}
                        data-testid={'review-item-see-more'}
                    >
                        {t(expand ? 'seeLess' : 'seeMore')}
                    </Button>
                ) : null}
                {sanitizedContent.length && sanitizedTranslatedContent && !isPreview ? (
                    <Button
                        size={'sm'}
                        mt={1}
                        onClick={() => setShowTranslated(!showTranslated)}
                        variant={'link'}
                        data-testid={'review-item-show-original'}
                    >
                        {t(showTranslated ? 'showOriginal' : 'showTranslation')}
                    </Button>
                ) : null}
                {!isPreview ? (
                    <VStack align={'start'} gap={1} mt={2} mb={{ base: 1, md: 2 }}>
                        {apartmentInfo?.name ? (
                            <HStack gap={3} data-testid={'review-item-room-name'}>
                                <IconsLinear.DoubleBed size={'xs'} />
                                <Text as={'span'} fontSize={'xs'} mt={0.5}>
                                    {apartmentInfo.name}
                                </Text>
                            </HStack>
                        ) : null}
                        {guestCategoryInfo ? (
                            <HStack gap={3} data-testid={'review-item-guest-category'}>
                                {getGuestCategoryIcon({ type: guestCategoryInfo.type })}
                                <Text as={'span'} fontSize={'xs'} mt={0.5}>
                                    {t(`guestCategories.${guestCategoryInfo.translationKey}`)}
                                </Text>
                            </HStack>
                        ) : null}
                    </VStack>
                ) : null}
            </Box>
        );
    },
);

ReviewItem.displayName = 'ReviewItem';
